/**
 * The playback clock. Everything else on this screen is indexed against it.
 *
 * Three decisions carry the whole design of this file.
 *
 * **1. A real `<audio>` element on a real URL, never a blob.**
 * `artifactUrl()` points at the BFF's byte-range proxy. The element opens the
 * stream, reads `content-range` to learn the total size, and then issues a
 * fresh ranged request every time the user scrubs — which is why an hour-long
 * recording is seekable a second after the page loads. `fetch()`-ing the whole
 * thing into an object URL would work on the 190 KB reference recording and
 * quietly become a 40 MB download on a real one.
 *
 * **1b. …or a real `<video>` element, driven by exactly the same code.**
 * When a session has a retained-video composite it becomes the element this
 * hook drives, and nothing else on the screen changes. See `attach`. There is
 * never more than ONE media element on this page: two would mean two clocks,
 * and the transcript can only follow one of them.
 *
 * **2. Time is published through a subscription, not through React state.**
 * Word-level highlighting needs the clock at animation rate. Putting that in
 * `useState` would re-render the transcript, the ribbon, the rail and the
 * charts sixty times a second. Instead the clock is a ref plus a subscriber
 * list: the playhead moves itself with a direct style write (zero renders), and
 * components that need a DERIVED value (which word is lit, which turn is
 * active) subscribe through `useTimeDerived` and re-render only when that
 * derived value actually changes — a few times a second at most.
 */

import * as React from 'react'

export interface Player {
  /**
   * Ref callback for the `<audio>` OR `<video>` element.
   *
   * `HTMLMediaElement`, not `HTMLAudioElement`, and the widening is the whole
   * mechanism behind video playback on this screen. `<video>` is not a subtype
   * of `<audio>` — they are siblings under `HTMLMediaElement` — but every API
   * this hook touches (`currentTime`, `duration`, `play`, `playbackRate`, the
   * whole event set) is defined on the base. So the clock does not know or care
   * which one it is driving, and the screen keeps ONE player, one timeline and
   * one set of subscribers whichever medium is on screen.
   *
   * That is what makes "the transcript follows whichever is playing" true by
   * construction rather than by synchronising two elements — which is the bug
   * this design exists to avoid.
   */
  attach: (element: HTMLMediaElement | null) => void
  /** Called on every clock tick. Returns an unsubscribe. */
  subscribe: (listener: () => void) => () => void
  /** The current position. Read inside a subscriber; never stored in state. */
  getTimeMs: () => number

  /** Best known total length. See `durationMs` below for where it comes from. */
  durationMs: number
  playing: boolean
  /** Metadata has arrived, so seeking will land where it is asked to. */
  ready: boolean
  /** Buffering. Distinguished from paused so the UI can say which it is. */
  waiting: boolean
  error: string | null
  rate: number

  play(): void
  pause(): void
  toggle(): void
  setRate(rate: number): void
  /** Absolute seek. `play` starts playback too — what a transcript click wants. */
  seekMs(ms: number, options?: { play?: boolean }): void
  /** Relative seek, clamped. Used by the ±10s buttons and the arrow keys. */
  nudge(deltaMs: number): void
}

export function useAudioPlayer(options: {
  src: string | null
  /**
   * The length the CATALOG says this artifact is.
   *
   * Used until the element reports its own, and as a permanent fallback if it
   * never does. Ogg carries no duration in its header — the browser has to
   * range-request the last page and read its granule position to work one out,
   * and a proxy that mishandled that would leave `duration` at `Infinity` and
   * the timeline unrenderable. `audio_pipeline.artifacts[].duration_ms` is
   * already on screen, costs nothing, and means the ribbon has a scale before
   * the first byte arrives.
   */
  fallbackDurationMs: number | null
}): Player {
  const { src, fallbackDurationMs } = options

  const elementRef = React.useRef<HTMLMediaElement | null>(null)
  const timeRef = React.useRef(0)
  const listenersRef = React.useRef(new Set<() => void>())
  const frameRef = React.useRef<number | null>(null)
  /** A seek asked for before metadata existed. Applied when it arrives. */
  const pendingSeekRef = React.useRef<number | null>(null)

  const [playing, setPlaying] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [waiting, setWaiting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [rate, setRateState] = React.useState(1)
  const [elementDurationMs, setElementDurationMs] = React.useState<number | null>(null)

  const emit = React.useCallback(() => {
    for (const listener of listenersRef.current) listener()
  }, [])

  const publish = React.useCallback(() => {
    const element = elementRef.current
    if (!element) return
    timeRef.current = element.currentTime * 1000
    emit()
  }, [emit])

  /* -- the animation loop, running only while there is motion to show ------- */

  const stopLoop = React.useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [])

  const startLoop = React.useCallback(() => {
    if (frameRef.current !== null) return
    const step = () => {
      publish()
      frameRef.current = requestAnimationFrame(step)
    }
    frameRef.current = requestAnimationFrame(step)
  }, [publish])

  React.useEffect(() => stopLoop, [stopLoop])

  /* -- element wiring ------------------------------------------------------ */

  // Stable on purpose. A ref callback whose identity changes is detached and
  // reattached on every render that changes it, so the playback RATE is applied
  // in an effect below rather than captured here.
  const attach = React.useCallback((element: HTMLMediaElement | null) => {
    elementRef.current = element
  }, [])

  // Bound as React props on the element in `player.tsx` would scatter this
  // logic across the JSX; keeping the handlers here means the hook owns the
  // whole state machine and the component just renders it.
  React.useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const onLoaded = () => {
      setReady(true)
      setError(null)
      const seconds = element.duration
      if (Number.isFinite(seconds) && seconds > 0) setElementDurationMs(seconds * 1000)
      const pending = pendingSeekRef.current
      if (pending !== null) {
        pendingSeekRef.current = null
        element.currentTime = pending / 1000
      }
      publish()
    }
    const onPlay = () => {
      setPlaying(true)
      setWaiting(false)
      startLoop()
    }
    const onPause = () => {
      setPlaying(false)
      stopLoop()
      publish()
    }
    const onEnded = () => {
      setPlaying(false)
      stopLoop()
      publish()
    }
    const onError = () => {
      stopLoop()
      setPlaying(false)
      setError(
        'the recording could not be loaded — it may have passed its retention date, or the control plane may be unreachable',
      )
    }

    element.addEventListener('loadedmetadata', onLoaded)
    element.addEventListener('durationchange', onLoaded)
    element.addEventListener('play', onPlay)
    element.addEventListener('playing', onPlay)
    element.addEventListener('pause', onPause)
    element.addEventListener('ended', onEnded)
    element.addEventListener('error', onError)
    element.addEventListener('waiting', () => setWaiting(true))
    element.addEventListener('canplay', () => setWaiting(false))
    // `seeked` and `timeupdate` keep the clock honest while PAUSED, when the
    // rAF loop is deliberately not running.
    element.addEventListener('seeked', publish)
    element.addEventListener('timeupdate', publish)

    return () => {
      element.removeEventListener('loadedmetadata', onLoaded)
      element.removeEventListener('durationchange', onLoaded)
      element.removeEventListener('play', onPlay)
      element.removeEventListener('playing', onPlay)
      element.removeEventListener('pause', onPause)
      element.removeEventListener('ended', onEnded)
      element.removeEventListener('error', onError)
      element.removeEventListener('seeked', publish)
      element.removeEventListener('timeupdate', publish)
    }
  }, [publish, startLoop, stopLoop, src])

  // Applied after mount and after every change, including the first render of a
  // newly mounted element.
  React.useEffect(() => {
    if (elementRef.current) elementRef.current.playbackRate = rate
  }, [rate, src])

  // A new source is a new recording: drop everything we learned about the old
  // one, or the ribbon keeps the previous session's length.
  React.useEffect(() => {
    timeRef.current = 0
    pendingSeekRef.current = null
    setPlaying(false)
    setReady(false)
    setError(null)
    setElementDurationMs(null)
    emit()
  }, [src, emit])

  /* -- the public surface -------------------------------------------------- */

  const durationMs = elementDurationMs ?? fallbackDurationMs ?? 0

  const play = React.useCallback(() => {
    const element = elementRef.current
    if (!element) return
    void element.play().catch(() => {
      // Autoplay policy, or a source that will not decode. `error` is already
      // set by the error handler in the second case; in the first, staying
      // paused is the correct and only outcome.
      setPlaying(false)
    })
  }, [])

  const pause = React.useCallback(() => {
    elementRef.current?.pause()
  }, [])

  const toggle = React.useCallback(() => {
    const element = elementRef.current
    if (!element) return
    if (element.paused) play()
    else element.pause()
  }, [play])

  const seekMs = React.useCallback(
    (ms: number, seekOptions?: { play?: boolean }) => {
      const element = elementRef.current
      if (!element) return
      const bounded = Math.max(0, durationMs > 0 ? Math.min(ms, durationMs) : ms)
      timeRef.current = bounded
      emit()
      if (element.readyState === 0) {
        // No metadata yet, so assigning `currentTime` would be discarded.
        // Remembered instead and applied on `loadedmetadata`.
        pendingSeekRef.current = bounded
      } else {
        element.currentTime = bounded / 1000
      }
      if (seekOptions?.play) play()
    },
    [durationMs, emit, play],
  )

  const nudge = React.useCallback(
    (deltaMs: number) => {
      seekMs(timeRef.current + deltaMs)
    },
    [seekMs],
  )

  const setRate = React.useCallback((next: number) => {
    setRateState(next)
    if (elementRef.current) elementRef.current.playbackRate = next
  }, [])

  const subscribe = React.useCallback((listener: () => void) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const getTimeMs = React.useCallback(() => timeRef.current, [])

  return React.useMemo(
    () => ({
      attach,
      subscribe,
      getTimeMs,
      durationMs,
      playing,
      ready,
      waiting,
      error,
      rate,
      play,
      pause,
      toggle,
      setRate,
      seekMs,
      nudge,
    }),
    [
      attach,
      subscribe,
      getTimeMs,
      durationMs,
      playing,
      ready,
      waiting,
      error,
      rate,
      play,
      pause,
      toggle,
      setRate,
      seekMs,
      nudge,
    ],
  )
}

/**
 * Subscribe to a value DERIVED from the clock, and re-render only when that
 * value changes.
 *
 * This is the mechanism that makes word highlighting affordable. `compute` runs
 * on every tick — cheap, a binary search — but the component re-renders only
 * when the answer is different from last time, which for "which word is lit" is
 * a handful of times a second rather than sixty.
 *
 * `compute` MUST be stable (wrap it in `useCallback`), or the subscription is
 * torn down and rebuilt on every render.
 */
export function useTimeDerived<T>(player: Player, compute: (timeMs: number) => T): T {
  const [value, setValue] = React.useState(() => compute(player.getTimeMs()))

  React.useEffect(() => {
    const update = () => {
      const next = compute(player.getTimeMs())
      setValue((previous) => (Object.is(previous, next) ? previous : next))
    }
    update()
    return player.subscribe(update)
  }, [player, compute])

  return value
}

/**
 * Write the clock straight into the DOM, with no render at all.
 *
 * For the things that must move smoothly and carry no other state — the
 * playhead line and the ticking `0:07` — a ref plus a style write is both
 * faster and simpler than any amount of memoised rendering.
 */
export function useClockEffect(
  player: Player,
  apply: (timeMs: number) => void,
  deps: React.DependencyList,
): void {
  const applyRef = React.useRef(apply)
  applyRef.current = apply

  React.useEffect(() => {
    const run = () => applyRef.current(player.getTimeMs())
    run()
    return player.subscribe(run)
  }, [player, ...deps])
}
